import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { firstValueFrom } from "rxjs";
import { HttpService } from "src/app/shared/services/http.service";
import {
	applyOwnStaffNumberFailure,
	applyStaffLookupFailure,
	isInstructorOwnStaffNumber,
	isStaffLookupErrorPayload,
	isStaffLookupResponseValid,
	staffLookupRolesForForm,
} from "src/app/shared/services/staff-lookup.helper";

/** Clears candidate fields populated from a successful staff-number lookup. */
export function clearCandidateStaffDerivedFields(candidateForm: UntypedFormGroup): void {
	const patch = (name: string, value: unknown) => {
		candidateForm.get(name)?.patchValue(value, { emitEvent: false });
	};

	patch("name", "");
	patch("rank", "");
	patch("pilotProfile", "");
	patch("licenseNo", "");
	patch("licenseType", "");
	patch("licenseExpiryDate", null);
}

/**
 * Re-validates staff numbers after restoring a draft. Invalid lookups mark the staff
 * control with invalidStaffId and clear derived fields so Start Rating stays disabled.
 */
export async function validateDraftCandidateStaffNumbers(
	candidates: UntypedFormArray,
	httpService: HttpService,
	ownStaffNumber?: string,
	formType?: string,
): Promise<void> {
	const lookups = candidates.controls.map(async (ctrl) => {
		const candidateForm = ctrl as UntypedFormGroup;
		const staffCtrl = candidateForm.get("staffNumber");
		if (!staffCtrl) return;

		const staffNumber = (staffCtrl.value ?? "").toString().trim();
		if (staffNumber.length !== 7) return;

		if (isInstructorOwnStaffNumber(staffNumber, ownStaffNumber)) {
			clearCandidateStaffDerivedFields(candidateForm);
			applyOwnStaffNumberFailure(staffCtrl);
			return;
		}

		if (!navigator.onLine) return;

		try {
			const resp = await firstValueFrom(
				httpService.getByStaffNumber(staffNumber, staffLookupRolesForForm(formType)),
			);
			if (!isStaffLookupResponseValid(resp) || isStaffLookupErrorPayload(resp)) {
				clearCandidateStaffDerivedFields(candidateForm);
				applyStaffLookupFailure(staffCtrl);
			}
		} catch {
			clearCandidateStaffDerivedFields(candidateForm);
			applyStaffLookupFailure(staffCtrl);
		}
	});

	await Promise.all(lookups);
}
